import { utils, writeFile } from 'xlsx'

export type EmpresaSummaryExport = {
    empresa: string
    total: number
}

export type MesAnoSummaryExport = {
    mesAno: string
    sortKey: number
    empresas: EmpresaSummaryExport[]
    total: number
}

export type ClassificadoSummaryExport = {
    classificado: string
    periodos: MesAnoSummaryExport[]
    total: number
}

export type TotalMesExport = {
    mesAno: string
    total: number
    sortKey: number
}

const getCurrentDateFormatted = () => {
    const currentDate = new Date()
    const day = currentDate.getDate().toString().padStart(2, '0')
    const month = (currentDate.getMonth() + 1).toString().padStart(2, '0')
    const year = currentDate.getFullYear().toString()
    return `${day}${month}${year}`
}

const columnLetter = (index: number) => {
    let letter = ''
    let n = index

    while (n >= 0) {
        letter = String.fromCharCode((n % 26) + 65) + letter
        n = Math.floor(n / 26) - 1
    }

    return letter
}

export const useSummaryByClassificadoSpreadsheets = () => {
    const onDownload = (
        summary: ClassificadoSummaryExport[],
        totalsByMonth: TotalMesExport[],
        grandTotal: number
    ) => {
        const workbook = utils.book_new()
        const formatMoneyAccounting = '_-* #,##0.00_-;-* #,##0.00_-;_-* "-"??_-;_-@_-'
        const months = totalsByMonth.map((item) => item.mesAno)

        const header = ['Classificado', 'Empresa', ...months, 'Total']
        const aoa: (string | number)[][] = [header]

        summary.forEach((group) => {
            const empresas = Array.from(
                new Set(
                    group.periodos.flatMap((periodo) =>
                        periodo.empresas.map((empresa) => empresa.empresa)
                    )
                )
            ).sort((a, b) => a.localeCompare(b))

            empresas.forEach((empresa) => {
                const row: (string | number)[] = [group.classificado, empresa]
                let rowTotal = 0

                months.forEach((mesAno) => {
                    const periodo = group.periodos.find((item) => item.mesAno === mesAno)
                    const valor =
                        periodo?.empresas.find((item) => item.empresa === empresa)?.total || 0
                    row.push(valor)
                    rowTotal += valor
                })

                row.push(rowTotal)
                aoa.push(row)
            })

            const classificadoTotalRow: (string | number)[] = [group.classificado, 'Total']
            months.forEach((mesAno) => {
                const periodo = group.periodos.find((item) => item.mesAno === mesAno)
                classificadoTotalRow.push(periodo?.total || 0)
            })
            classificadoTotalRow.push(group.total)
            aoa.push(classificadoTotalRow)
        })

        const generalTotalRow: (string | number)[] = ['', 'Total Geral']
        months.forEach((mesAno) => {
            const totalMes = totalsByMonth.find((item) => item.mesAno === mesAno)?.total || 0
            generalTotalRow.push(totalMes)
        })
        generalTotalRow.push(grandTotal)
        aoa.push(generalTotalRow)

        const worksheet = utils.aoa_to_sheet(aoa)
        worksheet['!cols'] = [
            { wch: 25 },
            { wch: 18 },
            ...months.map(() => ({ wch: 14 })),
            { wch: 16 },
        ]

        for (let rowIndex = 2; rowIndex <= aoa.length; rowIndex++) {
            for (let colIndex = 2; colIndex < header.length; colIndex++) {
                const cellAddress = `${columnLetter(colIndex)}${rowIndex}`
                if (worksheet[cellAddress]) {
                    worksheet[cellAddress].z = formatMoneyAccounting
                    worksheet[cellAddress].t = 'n'
                }
            }
        }

        utils.book_append_sheet(workbook, worksheet, 'Resumo')

        writeFile(workbook, `Resumo_por_Classificado_${getCurrentDateFormatted()}.xlsx`, {
            compression: true,
            cellStyles: true,
        })
    }

    return {
        onDownload,
    }
}
